import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDown, ChevronRight, Search, X, ExternalLink } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

/**
 * SPT-PTE-2026-0044 — Calibration Due grouped-by-customer view (Next 31/61
 * days tabs only). Same look & feel as DataTable (search box, per-column
 * filters, pager) but rows are grouped under one row per customer with a
 * chevron to expand/collapse the tools due beneath it. Clicking the
 * customer name (not the row/chevron) opens a pre-filled Receiving/New Job
 * tab via `onOpenCustomer`.
 */
export function GroupedCalDueTable({
  endpoint, params = {}, refreshSignal = 0,
  columns, onRowClick, onOpenCustomer, toolbar,
  testid = "grouped-due", emptyText = "Nothing due in this window.",
  searchPlaceholder = "Search customer, serial, asset, part, description…",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [colSearch, setColSearch] = useState({});
  const [colDebounced, setColDebounced] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [opening, setOpening] = useState(null); // customer_id being prefilled
  const paramKey = JSON.stringify(params);

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { const t = setTimeout(() => setColDebounced(colSearch), 300); return () => clearTimeout(t); }, [colSearch]);
  const colDebKey = JSON.stringify(colDebounced);
  useEffect(() => { setPage(0); }, [debounced, colDebKey, paramKey, refreshSignal]);

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get(endpoint, { params })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [endpoint, paramKey]); // eslint-disable-line
  useEffect(() => { fetchData(); }, [fetchData, refreshSignal]);

  const toolCols = useMemo(() => columns.filter((c) => c.key !== "customer_name"), [columns]);
  const searchableKeys = useMemo(() => toolCols.filter((c) => c.searchable !== false).map((c) => c.key), [toolCols]);

  // Filter tool rows, then bucket into per-customer groups.
  const groups = useMemo(() => {
    const anyFilter = !!debounced || Object.values(colDebounced).some(Boolean);
    let r = rows;
    Object.entries(colDebounced).forEach(([k, v]) => {
      if (!v) return;
      const t = String(v).toLowerCase();
      r = r.filter((row) => String(row[k] ?? "").toLowerCase().includes(t));
    });
    if (debounced) {
      const t = debounced.toLowerCase();
      r = r.filter((row) =>
        String(row.customer_name || "").toLowerCase().includes(t) ||
        searchableKeys.some((k) => String(row[k] ?? "").toLowerCase().includes(t)));
    }
    const map = new Map();
    r.forEach((row) => {
      const gid = row.customer_id || row.customer_name || "unknown";
      if (!map.has(gid)) {
        map.set(gid, {
          customerId: row.customer_id || null,
          customerName: row.customer_name || "Unknown customer",
          customerNumber: row.customer_number || null,
          rows: [],
        });
      }
      map.get(gid).rows.push(row);
    });
    const arr = Array.from(map.values());
    arr.forEach((g) => {
      g.rows.sort((a, b) => String(a.calibration_due_date || "").localeCompare(String(b.calibration_due_date || "")));
      g.soonest = g.rows[0]?.calibration_due_date || null;
      g.overdue = g.rows.some((x) => x.days_remaining != null && x.days_remaining < 0);
    });
    arr.sort((a, b) => a.customerName.localeCompare(b.customerName));
    return { list: arr, anyFilter, toolCount: r.length };
  }, [rows, debounced, colDebounced, searchableKeys]);

  const totalGroups = groups.list.length;
  const pageGroups = groups.list.slice(page * pageSize, page * pageSize + pageSize);

  const toggle = (gid) => setExpanded((s) => {
    const n = new Set(s);
    n.has(gid) ? n.delete(gid) : n.add(gid);
    return n;
  });

  const openCustomer = async (g) => {
    if (!g.customerId) { toast.error("This tool has no linked customer record — open it directly instead."); return; }
    setOpening(g.customerId);
    try { await onOpenCustomer(g); } finally { setOpening(null); }
  };

  return (
    <div data-testid={testid}>
      <div className="ftabs" style={{ alignItems: "center" }}>
        {toolbar}
        <span style={{ flex: 1 }} />
        <div className="search" style={{ maxWidth: 300 }}>
          <Search size={15} />
          <input placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} data-testid={`${testid}-search`} />
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="dt">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Customer</th>
              {toolCols.map((c) => <th key={c.key}>{c.label}</th>)}
            </tr>
            <tr className="col-search-row">
              <th style={{ background: "var(--bg-soft, #fafafa)" }}></th>
              <th style={{ padding: "4px 8px", background: "var(--bg-soft, #fafafa)" }} />
              {toolCols.map((c) => (
                <th key={c.key} style={{ padding: "4px 8px", background: "var(--bg-soft, #fafafa)" }}>
                  {c.colSearch && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--bg, #fff)", borderRadius: 6, padding: "2px 6px", border: "1px solid var(--line, #eee)" }}>
                      <Search size={11} color="var(--text-3)" />
                      <input
                        value={colSearch[c.key] || ""}
                        onChange={(e) => setColSearch({ ...colSearch, [c.key]: e.target.value })}
                        placeholder={`Filter ${c.label.toLowerCase()}…`}
                        style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 11.5, padding: "2px 0" }}
                        data-testid={`${testid}-colsearch-${c.key}`} />
                      {colSearch[c.key] && (
                        <button className="icon-btn" style={{ width: 16, height: 16 }} onClick={() => setColSearch({ ...colSearch, [c.key]: "" })}><X size={10} /></button>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={toolCols.length + 2} className="t-mut" style={{ padding: 30, textAlign: "center" }}>Loading…</td></tr>}
            {!loading && pageGroups.length === 0 && <tr><td colSpan={toolCols.length + 2} className="t-mut" style={{ padding: 30, textAlign: "center" }}>{emptyText}</td></tr>}
            {!loading && pageGroups.map((g) => {
              const gid = g.customerId || g.customerName;
              const isOpen = expanded.has(gid) || groups.anyFilter;
              return (
                <React.Fragment key={gid}>
                  <tr data-testid={`${testid}-group-row`} style={{ background: "var(--card-2)", cursor: "pointer" }} onClick={() => toggle(gid)}>
                    <td>{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); openCustomer(g); }}
                          disabled={opening === g.customerId}
                          data-testid={`${testid}-customer-link-${gid}`}
                          title="Open a pre-filled New Job tab for this customer"
                          style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--gold-deep)", fontWeight: 700, font: "inherit", display: "inline-flex", alignItems: "center", gap: 5 }}>
                          {g.customerNumber && <span className="mono" style={{ fontSize: 11, opacity: 0.8 }}>{g.customerNumber}</span>}
                          {g.customerName}
                          {opening === g.customerId ? <span className="t-mut" style={{ fontSize: 11 }}>opening…</span> : <ExternalLink size={12} />}
                        </button>
                        <span className="tag-soft" style={{ fontSize: 11 }} data-testid={`${testid}-group-count-${gid}`}>{g.rows.length} tool{g.rows.length !== 1 ? "s" : ""}</span>
                      </span>
                    </td>
                    <td colSpan={toolCols.length} className="t-mut" style={{ fontSize: 12 }}>
                      Soonest: <b style={{ color: g.overdue ? "var(--red)" : "var(--text-1)" }}>{fmtDate(g.soonest)}</b>
                    </td>
                  </tr>
                  {isOpen && g.rows.map((row) => (
                    <tr key={row.id} data-testid={`${testid}-row`} onClick={onRowClick ? () => onRowClick(row) : undefined} style={onRowClick ? { cursor: "pointer" } : undefined}>
                      <td></td>
                      <td></td>
                      {toolCols.map((c) => (
                        <td key={c.key} style={{ textAlign: c.align }} className={c.mono ? "mono" : ""}>
                          {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
        {totalGroups > pageSize || page > 0 ? (
          <>
            <button className="btn ghost sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid={`${testid}-prev`}>← Prev</button>
            <span className="t-mut">{page * pageSize + 1}–{Math.min(totalGroups, page * pageSize + pageGroups.length)} of {totalGroups} customers ({groups.toolCount} tools)</span>
            <button className="btn ghost sm" disabled={(page + 1) * pageSize >= totalGroups} onClick={() => setPage((p) => p + 1)} data-testid={`${testid}-next`}>Next →</button>
          </>
        ) : <span className="t-mut">{totalGroups} customer{totalGroups !== 1 ? "s" : ""} · {groups.toolCount} tool{groups.toolCount !== 1 ? "s" : ""} due</span>}
        <span style={{ flex: 1 }} />
        <label className="t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Customers per page
          <select className="input" style={{ padding: "4px 8px", height: "auto" }} value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))} data-testid={`${testid}-page-size`}>
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
